import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { api } from "../../../Config/Api";
import { Order, OrderItem } from "../../../types/orderTypes";

const orderFlow = ["PENDING", "PLACED", "CONFIRMED", "SHIPPED", "DELIVERED"];

const sectionPaperSx = {
  p: 3,
  borderRadius: "20px",
};

const SellerOrderView = () => {
  const { orderId } = useParams();
  const jwt = localStorage.getItem("jwt") || "";

  const [order, setOrder] = React.useState<Order | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [courierPartner, setCourierPartner] = React.useState("");
  const [trackingNumber, setTrackingNumber] = React.useState("");
  const [customerPreviousOrders, setCustomerPreviousOrders] = React.useState<Order[]>([]);
  const [previousOrdersLoading, setPreviousOrdersLoading] = React.useState(false);

  const [refundDialogOpen, setRefundDialogOpen] = React.useState(false);
  const [refundConfirmOpen, setRefundConfirmOpen] = React.useState(false);
  const [refundAmount, setRefundAmount] = React.useState("");
  const [refundFullAmount, setRefundFullAmount] = React.useState(false);
  const [refundInitiated, setRefundInitiated] = React.useState(false);

  React.useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/seller/orders/${orderId}`, {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        });

        setOrder(res.data);
      } catch (err: any) {
        console.log("Failed to load order details", err?.response || err);
        setError(err?.response?.data?.message || "Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId, jwt]);

  React.useEffect(() => {
    const fetchCustomerPreviousOrders = async () => {
      if (!order?.user?.email) return;

      try {
        setPreviousOrdersLoading(true);

        const res = await api.get(
          `/seller/orders/search-by-user?email=${encodeURIComponent(order.user.email)}`,
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
            },
          }
        );

        const orders = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.content)
          ? res.data.content
          : Array.isArray(res.data?.orders)
          ? res.data.orders
          : [];

        const filteredOrders = orders.filter((item: Order) => item.id !== order.id);
        setCustomerPreviousOrders(filteredOrders);
      } catch (err: any) {
        console.log("Failed to load customer previous orders", err?.response || err);
        setCustomerPreviousOrders([]);
      } finally {
        setPreviousOrdersLoading(false);
      }
    };

    fetchCustomerPreviousOrders();
  }, [order, jwt]);

  const handleDownloadShippingSlip = () => {
    if (!order) return;

    const slipContent = `
Shipping Slip
==============================

Order ID: ${order.id}
Customer Name: ${order.user?.fullName || "N/A"}
Customer Email: ${order.user?.email || "N/A"}
Customer Mobile No.: ${order.user?.mobile || "N/A"}

Shipping Address:
${order.shippingAddress?.name || ""}
${order.shippingAddress?.address || ""}
${order.shippingAddress?.city || ""}, ${order.shippingAddress?.state || ""} - ${order.shippingAddress?.pinCode || ""}
Mobile: ${order.shippingAddress?.mobile || ""}

Products:
${order.orderItems
  ?.map(
    (item: OrderItem, index: number) => `
${index + 1}. ${item.product?.title || ""}
   Color: ${item.product?.color || ""}
   Size: ${item.size || ""}
   Quantity: ${item.quantity || ""}
`
  )
  .join("\n")}
`;

    const blob = new Blob([slipContent], { type: "text/plain;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `shipping-slip-order-${order.id}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleTrackingSubmit = () => {
    console.log("Tracking Details Submitted:", {
      courierPartner,
      trackingNumber,
      orderId,
    });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";

    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}-${month}-${year} ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
  };

  const formatDateOnly = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const isStepCompleted = (step: string, currentStatus?: string) => {
    if (!currentStatus || currentStatus === "CANCELLED") return false;
    return orderFlow.indexOf(step) <= orderFlow.indexOf(currentStatus);
  };

  const isCurrentStep = (step: string, currentStatus?: string) => {
    if (!currentStatus || currentStatus === "CANCELLED") return false;
    return step === currentStatus;
  };

  const totalPreviousOrdersAmount = customerPreviousOrders.reduce(
    (sum, item) => sum + (item.totalSellingPrice || 0),
    0
  );

  const totalRefundableAmount = order?.totalSellingPrice || 0;
  const derivedPaymentStatus = order?.paymentDetails.paymentId ? "PAID" : "PENDING";

  const handleRefundAmountChange = (value: string) => {
    if (refundFullAmount || refundInitiated) return;

    if (value === "") {
      setRefundAmount("");
      return;
    }

    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setRefundAmount(value);
    }
  };

  const handleRefundFullAmountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setRefundFullAmount(checked);

    if (checked) {
      setRefundAmount(totalRefundableAmount.toFixed(2));
    } else if (!refundInitiated) {
      setRefundAmount("");
    }
  };

  const openRefundDialog = () => {
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    setRefundDialogOpen(false);
  };

  const openRefundConfirm = () => {
    if (!refundAmount || Number(refundAmount) <= 0) return;
    setRefundConfirmOpen(true);
  };

  const closeRefundConfirm = () => {
    setRefundConfirmOpen(false);
  };

  const confirmRefund = () => {
    setRefundInitiated(true);
    setRefundConfirmOpen(false);
    setRefundDialogOpen(false);
  };

  const checkRefundStatus = () => {
    alert(`Refund status for Rs.${refundAmount} is currently: INITIATED`);
  };

  if (loading) {
    return (
      <Box className="p-5">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="p-5">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box className="p-5">
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-5">
      <Paper sx={{ ...sectionPaperSx, mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          Order ID: #{order.id}
        </Typography>
        <Typography variant="h6" sx={{ mt: 1 }}>
          Customer Name: {order.user?.fullName || "N/A"}
        </Typography>
        <Typography variant="h6" sx={{ mt: 1 }}>
          Customer Email: {order.user?.email || "N/A"}
        </Typography>
        <Typography variant="h6" sx={{ mt: 1 }}>
          Customer Mobile No.: {order.user?.mobile || "N/A"}
        </Typography>
      </Paper>

      <Box className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Paper sx={sectionPaperSx}>
          <Typography variant="h6">Order Details</Typography>
          <Divider sx={{ my: 2 }} />

          <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Typography>
              <strong>Order Status:</strong> {order.orderStatus}
            </Typography>

            <Typography>
              <strong>Order Date:</strong> {formatDateTime(order.orderDate)}
            </Typography>

            <Typography>
              <strong>Total Items:</strong> {order.totalItem}
            </Typography>

            <Typography>
              <strong>Total MRP:</strong> Rs.{order.totalMrpPrice}
            </Typography>

            <Typography>
              <strong>Total Selling Price:</strong> Rs.{order.totalSellingPrice}
            </Typography>

            {order.discount !== undefined && (
              <Typography>
                <strong>Discount:</strong> Rs.{order.discount}
              </Typography>
            )}

            {order.deliverDate && (
              <Typography>
                <strong>Delivery Date:</strong> {formatDateOnly(order.deliverDate)}
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper sx={sectionPaperSx}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Typography variant="h6">Shipping Address</Typography>
            <Button variant="contained" onClick={handleDownloadShippingSlip}>
              Download Shipping Slip
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Box className="flex flex-col gap-y-2">
            <Typography>{order.shippingAddress?.name}</Typography>
            <Typography>
              {order.shippingAddress?.address}, {order.shippingAddress?.city}
            </Typography>
            <Typography>
              {order.shippingAddress?.state} - {order.shippingAddress?.pinCode}
            </Typography>
            <Typography>
              <strong>Mobile:</strong> {order.shippingAddress?.mobile}
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Paper sx={{ ...sectionPaperSx, mb: 4 }}>
        <Typography variant="h6">Payment</Typography>
        <Divider sx={{ my: 2 }} />

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
        >
          <Box>
            <Typography>
              <strong>Payment Status:</strong> {derivedPaymentStatus}
            </Typography>
            <Typography sx={{ mt: 1 }}>
              <strong>Refundable Amount:</strong> Rs.{totalRefundableAmount}
            </Typography>
            {refundInitiated && (
              <Typography sx={{ mt: 1, color: "success.main", fontWeight: 600 }}>
                Refund initiated for Rs.{refundAmount}
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              variant="contained"
              onClick={openRefundDialog}
              disabled={refundInitiated}
            >
              Initiate Refund
            </Button>

            {refundInitiated && (
              <Button variant="outlined" onClick={checkRefundStatus}>
                Check Refund Status
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Box className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <Box className="flex flex-col gap-4">
          <Paper sx={sectionPaperSx}>
            <Typography variant="h6">Product Details</Typography>
            <Divider sx={{ my: 2 }} />

            <Box
              className="flex flex-col gap-4"
              sx={
                order.orderItems && order.orderItems.length > 2
                  ? { maxHeight: 500, overflowY: "auto", pr: 1 }
                  : {}
              }
            >
              {order.orderItems?.map((orderItem: OrderItem) => (
                <Paper
                  key={orderItem.id}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: "16px" }}
                >
                  <Box className="flex flex-col md:flex-row gap-4">
                    <img
                      className="w-28 h-28 rounded-md object-cover"
                      src={orderItem.product?.images?.[0]}
                      alt={orderItem.product?.title}
                    />

                    <Box className="flex flex-col gap-y-1">
                      <Typography>
                        <strong>Product Title:</strong> {orderItem.product?.title}
                      </Typography>
                      <Typography>
                        <strong>Selling Price:</strong> Rs.
                        {orderItem.product?.sellingPrice}
                      </Typography>
                      <Typography>
                        <strong>MRP:</strong> Rs.{orderItem.mrpPrice}
                      </Typography>
                      <Typography>
                        <strong>Color:</strong> {orderItem.product?.color}
                      </Typography>
                      <Typography>
                        <strong>Size:</strong> {orderItem.size}
                      </Typography>
                      <Typography>
                        <strong>Quantity:</strong> {orderItem.quantity}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Box>

        <Paper sx={{ ...sectionPaperSx, minHeight: 300 }}>
          <Typography variant="h6">Tracking Details</Typography>
          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="courier-partner-label">Courier Partner</InputLabel>
              <Select
                labelId="courier-partner-label"
                value={courierPartner}
                label="Courier Partner"
                onChange={(e) => setCourierPartner(e.target.value)}
              >
                <MenuItem value="Delhivery">Delhivery</MenuItem>
                <MenuItem value="Blue Dart">Blue Dart</MenuItem>
                <MenuItem value="DTDC">DTDC</MenuItem>
                <MenuItem value="XpressBees">XpressBees</MenuItem>
                <MenuItem value="India Post">India Post</MenuItem>
                <MenuItem value="Ekart">Ekart</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Tracking Number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
            />

            <Box>
              <Button variant="contained" onClick={handleTrackingSubmit}>
                Submit
              </Button>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Order Progress
              </Typography>

              {order.orderStatus === "CANCELLED" ? (
                <Typography color="error">This order has been cancelled.</Typography>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  {orderFlow.map((step, index) => (
                    <React.Fragment key={step}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minWidth: 80,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            border: "2px solid",
                            borderColor: isCurrentStep(step, order.orderStatus)
                              ? "#1976d2"
                              : isStepCompleted(step, order.orderStatus)
                              ? "#2e7d32"
                              : "#bdbdbd",
                            backgroundColor: isCurrentStep(step, order.orderStatus)
                              ? "#1976d2"
                              : isStepCompleted(step, order.orderStatus)
                              ? "#2e7d32"
                              : "#ffffff",
                            color:
                              isCurrentStep(step, order.orderStatus) ||
                              isStepCompleted(step, order.orderStatus)
                                ? "#ffffff"
                                : "#616161",
                          }}
                        >
                          {index + 1}
                        </Box>

                        <Typography
                          variant="body2"
                          fontWeight={isCurrentStep(step, order.orderStatus) ? 700 : 500}
                          sx={{ mt: 1, textAlign: "center" }}
                          color={
                            isCurrentStep(step, order.orderStatus)
                              ? "primary.main"
                              : isStepCompleted(step, order.orderStatus)
                              ? "success.main"
                              : "text.secondary"
                          }
                        >
                          {step}
                        </Typography>
                      </Box>

                      {index < orderFlow.length - 1 && (
                        <Box
                          sx={{
                            flex: 1,
                            height: { xs: 24, md: 4 },
                            width: { xs: 4, md: "auto" },
                            alignSelf: "center",
                            backgroundColor:
                              isStepCompleted(orderFlow[index + 1], order.orderStatus)
                                ? "#2e7d32"
                                : "#e0e0e0",
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Paper sx={{ ...sectionPaperSx, mb: 4, width: { xs: "100%", lg: "50%" } }}>
        <Typography variant="h6">Customer&apos;s Previous Orders</Typography>
        <Divider sx={{ my: 2 }} />

        {previousOrdersLoading ? (
          <Typography>Loading previous orders...</Typography>
        ) : (
          <>
            <Typography sx={{ mb: 2 }}>
              <strong>Total Amount:</strong> Rs.{totalPreviousOrdersAmount}
            </Typography>

            {customerPreviousOrders.length === 0 ? (
              <Typography>No previous orders found</Typography>
            ) : (
              <TableContainer
                sx={
                  customerPreviousOrders.length > 10
                    ? { maxHeight: 420, overflowY: "auto" }
                    : {}
                }
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Order ID</strong></TableCell>
                      <TableCell><strong>Order Date</strong></TableCell>
                      <TableCell><strong>Order Status</strong></TableCell>
                      <TableCell><strong>Total Price</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customerPreviousOrders.map((previousOrder) => (
                      <TableRow key={previousOrder.id}>
                        <TableCell>
                          <Button
                            variant="text"
                            sx={{ p: 0, minWidth: "auto", textTransform: "none" }}
                            onClick={() =>
                              window.open(
                                `/seller/orders/view/${previousOrder.id}`,
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                          >
                            #{previousOrder.id}
                          </Button>
                        </TableCell>
                        <TableCell>{formatDateTime(previousOrder.orderDate)}</TableCell>
                        <TableCell>{previousOrder.orderStatus}</TableCell>
                        <TableCell>Rs.{previousOrder.totalSellingPrice}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      <Dialog open={refundDialogOpen} onClose={closeRefundDialog} fullWidth maxWidth="sm">
        <DialogTitle>Initiate Refund</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Refund Amount"
              value={refundAmount}
              onChange={(e) => handleRefundAmountChange(e.target.value)}
              disabled={refundFullAmount || refundInitiated}
              inputProps={{ inputMode: "decimal" }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={refundFullAmount}
                  onChange={handleRefundFullAmountChange}
                  disabled={refundInitiated}
                />
              }
              label="Refund the full amount"
            />

            {refundInitiated && (
              <Typography color="success.main" fontWeight={600}>
                Refund initiated for Rs.{refundAmount}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundDialog}>Cancel</Button>
          {!refundInitiated ? (
            <Button
              variant="contained"
              onClick={openRefundConfirm}
              disabled={!refundAmount || Number(refundAmount) <= 0}
            >
              Refund
            </Button>
          ) : (
            <Button variant="outlined" onClick={checkRefundStatus}>
              Check Refund Status
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={refundConfirmOpen} onClose={closeRefundConfirm}>
        <DialogTitle>Confirm Refund</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to initiate the refund of Rs.{refundAmount}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundConfirm}>No</Button>
          <Button variant="contained" onClick={confirmRefund}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerOrderView;